import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUpDown,
  Archive,
  CheckCircle2,
  EyeOff,
  FolderOpen,
  Layers,
  ListChecks,
  Megaphone,
  Pencil,
  RotateCcw,
  Trash2,
  UserMinus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '@/hooks/useApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill, type PillTone } from '@/components/ui/pill';
import { Tag, channelLabel } from '@/components/ui/tag';
import { MultiSelect } from '@/components/ui/multi-select';
import { Combobox } from '@/components/ui/combobox';
import { FilterField } from '@/components/ui/filter-field';
import { PageHeader } from '@/components/ui/page-header';
import { Pagination } from '@/components/ui/pagination';
import {
  RangeSelectHint,
  RowCheckbox,
  SelectAllCheckbox,
  selectableRowClass,
} from '@/components/ui/range-select';
import { EmptyState } from '@/components/ui/empty-state';
import { ActiveFilters, type ActiveFilter } from '@/components/ui/active-filters';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
  DataTableSkeleton,
} from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PlatformIcon } from '@/components/PlatformIcon';
import { cn } from '@/lib/utils';
import {
  folderMessagedCount,
  folderProgress,
  folderRemovableCount,
  folderUnmessagedCount,
  leftoverBreakdown,
  leftoverVerdict,
  leftoverVerdictText,
  NEARLY_DONE_PERCENT,
  sortFolders,
  useAdminProspectFolders,
  useAdminProspects,
  useCloseFolder,
  useDeleteProspects,
  useDismissProspects,
  useFolderGuidance,
  useReassignProspects,
  useSaveFolderGuidance,
  useSelectableCampaigns,
  useUndismissProspect,
  type AdminFolderSummary,
  type FolderSort,
  type AdminProspect,
  type AdminProspectFilters,
} from '@/hooks/useAdminProspects';
import { useAdminInvitersPaged, useAdminTeams, type OutreachTeam } from '@/hooks/useAdmin';
import { useUrlState } from '@/hooks/useUrlState';
import { useMainScopeLabel, useTeamScope } from '@/hooks/useTeamScope';
import { useDebounce } from '@/hooks/useDebounce';
import {
  applyToSet,
  useRangeSelection,
  type RangeCheckboxProps,
  type RangeRowProps,
} from '@/hooks/useRangeSelection';
import { keepOnly } from '@/lib/rangeSelect';
import { PROSPECT_STATUS_LABELS, prospectStatusLabel, teamLabel } from '@/lib/labels';
import { daysSince, formatDate, formatDateTime } from '@/lib/dates';
import {
  STALE_FOLDER_DAYS,
  confirmCountMatches,
  formatDayCount,
  isStaleFolder,
  plural,
  skippedIds,
} from '@/lib/peoplePages';
import { INVITE_CHANNELS, type DiscoveryProspectStatus, type InviteChannel } from '@/api/types';
import { inviteLink } from '@/lib/moneyPages';
import { FOLDER_PURPOSES, FOLDER_PURPOSE_LABELS, folderPurposeOf, purposeFilterOf } from '@/lib/folderPurpose';
import { prospectProfileUrl } from '@/components/discovery-queue/profile';

const PAGE_SIZE = 50;

// Pill (state) rather than Tag (metadata) — DESIGN.md's "Tags vs Pills".
const STATUS_TONES: Record<DiscoveryProspectStatus, PillTone> = {
  AVAILABLE: 'pending',
  CLAIMED: 'info',
  CONTACTED: 'info',
  PROMOTED: 'success',
  DISMISSED: 'rejected',
};

/** URL sentinel for "every status": the Leads tab defaults to Open. */
const ANY_STATUS = 'ANY';

/**
 * URL value for "nobody has messaged them yet" — Open plus Claimed. This is
 * what used to be the Queued filter: the leads a folder close-out removes.
 */
const UNMESSAGED = 'UNMESSAGED';
const UNMESSAGED_STATUSES = 'AVAILABLE,CLAIMED';

const STATUS_OPTIONS = [
  { value: UNMESSAGED, label: 'Not messaged yet' },
  ...(Object.keys(PROSPECT_STATUS_LABELS) as DiscoveryProspectStatus[]).map((s) => ({
    value: s,
    label: prospectStatusLabel(s),
  })),
];
const statusFilterLabel = (s: string): string => (s === UNMESSAGED ? 'Not messaged yet' : prospectStatusLabel(s));
const FLAG_OPTIONS = [
  { value: 'only', label: 'Marked irrelevant' },
  { value: 'none', label: 'Not marked' },
];
const AGE_OPTIONS = [
  { value: '7', label: '7+ days' },
  { value: '14', label: '14+ days' },
  { value: '30', label: '30+ days' },
];

type BulkKind = 'remove' | 'delete' | 'reassign';
type PageTab = 'folders' | 'leads';

/**
 * Admin view of the lead queue, in two tabs:
 *
 *   - Folders: one table row per exported folder (open, messaged, logged,
 *     oldest untouched), with the briefing editor. `?stale=1` (linked from
 *     Home) lands here, filtered to folders untouched 7+ days, oldest first.
 *     `?nearly=1` shows folders that are nearly done, with a read on whether
 *     their leftovers are worth keeping.
 *   - Leads: every lead, defaulting to Open, with bulk remove / delete /
 *     reassign.
 */
export function AdminProspects(): JSX.Element {
  const { team, locked, params: teamParams } = useTeamScope();
  const mainScopeLabel = useMainScopeLabel();
  // `team` is the same param useTeamScope reads; declared here so a team
  // change and the page reset land in one navigation.
  const [f, setF] = useUrlState({
    tab: 'folders',
    team: '',
    q: '',
    status: 'AVAILABLE',
    channel: '',
    flagged: '',
    age: '',
    folder: '',
    purpose: '',
    stale: false as boolean,
    nearly: false as boolean,
    fsort: 'attention',
    page: 1,
  });
  // `?stale=1` (linked from Home) always means the Folders tab.
  const tab: PageTab = !f.stale && f.tab === 'leads' ? 'leads' : 'folders';
  const status = f.status === ANY_STATUS ? '' : f.status;
  const purpose = purposeFilterOf(f.purpose);

  const [searchInput, setSearchInput] = useState(f.q);
  const debouncedSearch = useDebounce(searchInput.trim(), 300);
  useEffect(() => {
    if (debouncedSearch !== f.q) setF({ q: debouncedSearch, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  useEffect(() => {
    setSearchInput((cur) => (cur.trim() === f.q ? cur : f.q));
  }, [f.q]);

  const filters: AdminProspectFilters = {
    ...(status === UNMESSAGED
      ? { statuses: UNMESSAGED_STATUSES }
      : status
        ? { status: status as DiscoveryProspectStatus }
        : {}),
    ...(f.channel ? { channel: f.channel as InviteChannel } : {}),
    ...(f.folder ? { folderPublicId: f.folder } : {}),
    ...(purpose ? { folderPurpose: purpose } : {}),
    ...(team ? { assignedToTeam: team } : {}),
    // Main view: leads assigned to Influencer or Vyral stay out; the shared pool stays in.
    ...(teamParams.excludeTeam ? { excludeTeam: teamParams.excludeTeam } : {}),
    ...(f.q ? { search: f.q } : {}),
    ...(f.age ? { minAgeDays: Number(f.age) } : {}),
    ...(f.flagged === 'only' || f.flagged === 'none' ? { flagged: f.flagged } : {}),
    page: f.page,
    limit: PAGE_SIZE,
  };
  const filterKey = JSON.stringify(filters);
  const currentFilterKey = useRef(filterKey);
  currentFilterKey.current = filterKey;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<BulkKind | null>(null);
  const api = useApi();
  const [loadingAll, setLoadingAll] = useState(false);
  const [allReassignIds, setAllReassignIds] = useState<string[] | null>(null);

  async function selectAllForReassignment() {
    setLoadingAll(true);
    try {
      const ids = new Set<string>();
      let page = 1;
      let remaining = true;
      while (remaining) {
        const result = await api.getPaged<AdminProspect[]>('/admin/prospects', {
          ...filters, page, limit: 200,
        });
        result.data.filter((lead) => !isLoggedLead(lead)).forEach((lead) => ids.add(lead.publicId));
        remaining = result.data.length > 0 && page * 200 < result.meta.total;
        page += 1;
      }
      if (currentFilterKey.current !== filterKey) {
        toast.info('Filters changed. Select all again for the current filters.');
        return;
      }
      if (ids.size === 0) {
        toast.info('No eligible leads match these filters.');
        return;
      }
      setAllReassignIds(Array.from(ids));
      setDialog('reassign');
    } catch (err) {
      toast.error(`Couldn't select all leads: ${(err as Error).message}`);
    } finally {
      setLoadingAll(false);
    }
  }
  // A selection only means something on the page it was made on.
  useEffect(() => {
    setSelected(new Set());
  }, [filterKey]);

  const teams = useAdminTeams();
  const foldersQuery = useAdminProspectFolders();
  const listQuery = useAdminProspects(filters);

  // The folders the purpose filter keeps: both tabs and the Folder picker use these.
  const folders = useMemo(
    () => (foldersQuery.data ?? []).filter((x) => !purpose || folderPurposeOf(x.purpose) === purpose),
    [foldersQuery.data, purpose],
  );

  const rows = listQuery.data?.data ?? [];
  const total = listQuery.data?.meta?.total ?? 0;
  const teamName = (code: string): string =>
    teamLabel(code, (teams.data ?? []).find((t) => t.code === code)?.displayName);
  const folderName =
    (foldersQuery.data ?? []).find((x) => x.sourceFolderPublicId === f.folder)?.sourceFolderName ?? 'Selected folder';

  const rowIds = useMemo(() => rows.map((r) => r.publicId), [rows]);
  const lockedIds = useMemo(() => new Set(rows.filter(isLoggedLead).map((r) => r.publicId)), [rows]);
  const range = useRangeSelection({
    visible: rowIds,
    isSelected: (id) => selected.has(id),
    onApply: applyToSet(setSelected),
    isDisabled: (id) => lockedIds.has(id),
  });
  // Ticks only stand for rows on screen: a lead that left the page (removed,
  // logged, or swapped out when a refetch landed) drops out of the selection
  // instead of being acted on unseen.
  const selectableKey = range.selectable.join(',');
  useEffect(() => {
    setSelected((prev) => keepOnly(prev, range.selectable));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableKey]);

  const activeFilters: ActiveFilter[] = [
    f.folder ? { key: 'folder', label: `Folder: ${folderName}`, clear: () => setF({ folder: '', page: 1 }) } : null,
    // Open is the default, and the Status picker already says so.
    status && status !== 'AVAILABLE'
      ? { key: 'status', label: `Status: ${statusFilterLabel(status)}`, clear: () => setF({ status: ANY_STATUS, page: 1 }) }
      : null,
    team && !locked
      ? { key: 'team', label: `Team: ${teamName(team)}`, clear: () => setF({ team: '', page: 1 }) }
      : null,
    f.channel
      ? { key: 'channel', label: `Channel: ${channelLabel(f.channel)}`, clear: () => setF({ channel: '', page: 1 }) }
      : null,
    f.flagged
      ? {
          key: 'flagged',
          label: FLAG_OPTIONS.find((o) => o.value === f.flagged)?.label ?? 'Marked irrelevant',
          clear: () => setF({ flagged: '', page: 1 }),
        }
      : null,
    f.age ? { key: 'age', label: `In queue ${f.age}+ days`, clear: () => setF({ age: '', page: 1 }) } : null,
    f.q ? { key: 'q', label: `Handle: ${f.q}`, clear: () => { setSearchInput(''); setF({ q: '', page: 1 }); } } : null,
  ].filter((x): x is ActiveFilter => x !== null);

  const openCount = folders.reduce((n, x) => n + x.available, 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Lead queue"
        description="Leads exported from discovery folders, across every outreacher. Clear out what shouldn't be worked and hand leads to the right person."
      />

      <div className="flex flex-wrap items-center gap-2">
        <span id="purpose-filter" className="text-sm text-muted-foreground">
          Folders for
        </span>
        <div
          role="radiogroup"
          aria-labelledby="purpose-filter"
          className="inline-flex flex-wrap rounded-lg border border-input bg-[var(--input-bg)] p-0.5"
        >
          {(['', ...FOLDER_PURPOSES] as const).map((p) => (
            <button
              key={p || 'all'}
              type="button"
              role="radio"
              aria-checked={purpose === p}
              // A folder picked under another purpose would leave the list empty.
              onClick={() => setF({ purpose: p, folder: '', page: 1 })}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold transition-colors pointer-coarse:h-10',
                purpose === p
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {p ? FOLDER_PURPOSE_LABELS[p] : 'All'}
            </button>
          ))}
        </div>
      </div>

      <div role="tablist" aria-label="View" className="flex flex-wrap gap-1 border-b border-border">
        {(
          [
            { id: 'folders', label: 'Folders', count: foldersQuery.data ? folders.length : null },
            { id: 'leads', label: 'Leads', count: tab === 'leads' && !listQuery.isLoading ? total : openCount || null },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`panel-${t.id}`}
            onClick={() => setF({ tab: t.id, ...(t.id === 'leads' ? { stale: false } : {}) })}
            className={cn(
              '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-[120ms]',
              tab === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
            {t.count !== null && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-none text-muted-foreground">
                {t.count.toLocaleString()}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'folders' ? (
        <div role="tabpanel" id="panel-folders" aria-labelledby="tab-folders">
          <FoldersTable
            folders={folders}
            isLoading={foldersQuery.isLoading}
            error={foldersQuery.error as Error | null}
            staleOnly={f.stale}
            onStaleOnlyChange={(stale) => setF({ stale, ...(stale ? { fsort: 'oldest' } : {}) })}
            nearlyOnly={f.nearly}
            onNearlyOnlyChange={(nearly) => setF({ nearly, ...(nearly ? { fsort: 'progress' } : {}) })}
            sort={
              f.stale || f.fsort === 'oldest'
                ? 'oldest'
                : f.fsort === 'newest' || f.fsort === 'progress'
                  ? f.fsort
                  : 'attention'
            }
            onSortChange={(fsort) => setF({ fsort })}
            onViewLeads={(publicId) =>
              setF({ tab: 'leads', stale: false, folder: publicId ?? '', status: ANY_STATUS, page: 1 })
            }
            onReviewLeftovers={(publicId) => {
              // Every leftover, flagged or not, so the notes sit next to the
              // ones nobody marked. Filters that could hide some are cleared.
              setSearchInput('');
              setF({
                tab: 'leads',
                stale: false,
                folder: publicId,
                status: UNMESSAGED,
                flagged: '',
                age: '',
                channel: '',
                q: '',
                page: 1,
              });
            }}
          />
        </div>
      ) : (
        <section role="tabpanel" id="panel-leads" aria-labelledby="tab-leads" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-medium">
              {plural(total, 'lead')}
              {f.folder && <span className="font-normal text-muted-foreground"> in {folderName}</span>}
            </h2>
            <Button variant="outline" size="sm" disabled={loadingAll || listQuery.isFetching || total === 0}
              onClick={selectAllForReassignment}>
              {loadingAll ? 'Selecting leads…' : 'Select all matching leads to reassign'}
            </Button>
            {selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Bulk actions">
                <span className="text-sm text-muted-foreground">{selected.size} selected</span>
                <RangeSelectHint />
                <Button variant="outline" size="sm" onClick={() => setDialog('reassign')}>
                  <Users className="mr-1 h-3.5 w-3.5" /> Reassign
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDialog('remove')}>
                  <UserMinus className="mr-1 h-3.5 w-3.5" /> Remove from queue
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setDialog('delete')}>
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  Clear selection
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <FilterField label="Handle">
              <Input
                type="search"
                className="w-full md:w-56"
                aria-label="Search leads by handle"
                placeholder="Search handle…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </FilterField>
            <FilterField label="Status">
              <Combobox
                className="w-full md:w-40"
                value={status}
                onChange={(v) => setF({ status: v || ANY_STATUS, page: 1 })}
                options={STATUS_OPTIONS}
                allowAll
                allLabel="Any status"
                placeholder="Any status"
              />
            </FilterField>
            <FilterField label="Folder">
              <Combobox
                className="w-full md:w-52"
                value={f.folder}
                onChange={(v) => setF({ folder: v, page: 1 })}
                options={folders
                  .filter((x) => x.sourceFolderPublicId)
                  .map((x) => ({ value: x.sourceFolderPublicId ?? '', label: x.sourceFolderName ?? 'No folder' }))}
                allowAll
                allLabel="Any folder"
                placeholder="Any folder"
              />
            </FilterField>
            {!locked && (
              <FilterField label="Team">
                <Combobox
                  className="w-full md:w-40"
                  value={team}
                  onChange={(v) => setF({ team: v, page: 1 })}
                  options={(teams.data ?? [])
                    .filter((t) => t.isActive)
                    .map((t) => ({ value: t.code, label: t.displayName }))}
                  allowAll
                  allLabel={mainScopeLabel}
                  placeholder={mainScopeLabel}
                />
              </FilterField>
            )}
            <FilterField label="Channel">
              <Combobox
                className="w-full md:w-36"
                value={f.channel}
                onChange={(v) => setF({ channel: v, page: 1 })}
                options={INVITE_CHANNELS.map((c) => ({ value: c, label: channelLabel(c) }))}
                allowAll
                allLabel="Any channel"
                placeholder="Any channel"
              />
            </FilterField>
            <FilterField label="Irrelevant">
              <Combobox
                className="w-full md:w-44"
                value={f.flagged}
                onChange={(v) => setF({ flagged: v, page: 1 })}
                options={FLAG_OPTIONS}
                allowAll
                allLabel="Any"
                placeholder="Any"
              />
            </FilterField>
            <FilterField label="In queue for">
              <Combobox
                className="w-full md:w-32"
                value={f.age}
                onChange={(v) => setF({ age: v, page: 1 })}
                options={AGE_OPTIONS}
                allowAll
                allLabel="Any time"
                placeholder="Any time"
              />
            </FilterField>
          </div>

          <ActiveFilters
            filters={activeFilters}
            onClearAll={() => {
              setSearchInput('');
              setF({
                q: '',
                status: 'AVAILABLE',
                channel: '',
                flagged: '',
                age: '',
                folder: '',
                page: 1,
                ...(locked ? {} : { team: '' }),
              });
            }}
          />

          {listQuery.error ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              Couldn't load leads: {(listQuery.error as Error).message}
            </p>
          ) : !listQuery.isLoading && rows.length === 0 ? (
            <EmptyState
              icon={<Layers className="h-6 w-6" />}
              title="Nothing in the queue matches these filters."
              hint={activeFilters.length > 0 ? 'Try clearing a filter.' : 'Export leads from a discovery folder to fill the queue.'}
            />
          ) : (
            <DataTable aria-label="Leads" aria-busy={listQuery.isFetching}>
              <DataTableHeader>
                <tr>
                  <DataTableHeaderCell>
                    <span className="inline-flex items-center gap-2">
                      <SelectAllCheckbox
                        label="Select every lead on this page (logged leads excluded)"
                        state={range.headState}
                        disabled={range.selectable.length === 0}
                        onToggle={range.toggleAll}
                      />
                      Creator
                    </span>
                  </DataTableHeaderCell>
                  <DataTableHeaderCell>Status</DataTableHeaderCell>
                  <DataTableHeaderCell>Who has it</DataTableHeaderCell>
                  <DataTableHeaderCell title="Outreachers who have messaged this creator">Messaged by</DataTableHeaderCell>
                  <DataTableHeaderCell title="Outreachers who marked this lead wrong for its folder">
                    Irrelevant
                  </DataTableHeaderCell>
                  <DataTableHeaderCell>Folder</DataTableHeaderCell>
                  <DataTableHeaderCell title="Since the lead was exported to the queue">In queue for</DataTableHeaderCell>
                  <DataTableHeaderCell>Since last message</DataTableHeaderCell>
                  <DataTableHeaderCell>
                    <span className="sr-only">Actions</span>
                  </DataTableHeaderCell>
                </tr>
              </DataTableHeader>
              {listQuery.isLoading ? (
                <DataTableSkeleton cols={['w-32', 'w-16', 'w-24', 'w-20', 'w-10', 'w-24', 'w-14', 'w-14', 'w-12']} />
              ) : (
                <tbody className={cn(listQuery.isPlaceholderData && 'opacity-60 transition-opacity')}>
                  {rows.map((p) => (
                    <ProspectRow
                      key={p.publicId}
                      prospect={p}
                      teamName={teamName}
                      selected={selected.has(p.publicId)}
                      rowProps={range.rowProps(p.publicId)}
                      checkboxProps={range.checkboxProps(p.publicId)}
                    />
                  ))}
                </tbody>
              )}
            </DataTable>
          )}

          <Pagination page={f.page} limit={PAGE_SIZE} total={total} onPageChange={(page) => setF({ page })} />
        </section>
      )}

      {dialog && (
        <BulkActionDialog
          key={dialog}
          kind={dialog}
          publicIds={allReassignIds ?? Array.from(selected)}
          teams={teams.data ?? []}
          onClose={() => { setDialog(null); setAllReassignIds(null); }}
          onDone={() => {
            setDialog(null);
            setAllReassignIds(null);
            setSelected(new Set());
          }}
        />
      )}
    </div>
  );
}

// ── Folders tab ───────────────────────────────────────────────────────────

/** What "Close" sends when messaged-waiting leads go too. Logged (PROMOTED) is never sent. */
const CLOSE_WITH_MESSAGED: DiscoveryProspectStatus[] = ['AVAILABLE', 'CLAIMED', 'CONTACTED'];

/** Above this, finding a folder by eye stops working — offer a filter. */
const FOLDER_SEARCH_THRESHOLD = 8;

function FoldersTable({
  folders,
  isLoading,
  error,
  staleOnly,
  onStaleOnlyChange,
  nearlyOnly,
  onNearlyOnlyChange,
  sort,
  onSortChange,
  onViewLeads,
  onReviewLeftovers,
}: {
  folders: AdminFolderSummary[];
  isLoading: boolean;
  error: Error | null;
  staleOnly: boolean;
  onStaleOnlyChange: (stale: boolean) => void;
  nearlyOnly: boolean;
  onNearlyOnlyChange: (nearly: boolean) => void;
  sort: FolderSort;
  onSortChange: (sort: FolderSort) => void;
  onViewLeads: (publicId: string | null) => void;
  /** Leads tab, this folder, "Not messaged yet" — where leftovers get weeded. */
  onReviewLeftovers: (publicId: string) => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminFolderSummary | null>(null);
  // Folders picked for closing (by publicId; the "No folder" bucket can't be closed).
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [closing, setClosing] = useState<AdminFolderSummary[] | null>(null);
  // Finished folders (every lead logged or removed) are history, not work.
  const [showFinished, setShowFinished] = useState(false);
  const campaigns = useSelectableCampaigns();
  const campaignsDown = campaigns.data ? !campaigns.data.available : false;

  const staleCount = useMemo(() => folders.filter((x) => isStaleFolder(x)).length, [folders]);
  const nearlyCount = useMemo(() => folders.filter((x) => folderProgress(x).nearlyDone).length, [folders]);
  const finishedCount = useMemo(() => folders.filter((x) => folderRemovableCount(x) === 0).length, [folders]);
  const totals = useMemo(
    () =>
      folders.reduce(
        (acc, x) => ({
          open: acc.open + x.available,
          messaged: acc.messaged + folderMessagedCount(x),
          logged: acc.logged + x.promoted,
        }),
        { open: 0, messaged: 0, logged: 0 },
      ),
    [folders],
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = sortFolders(folders, sort, STALE_FOLDER_DAYS);
    if (staleOnly) out = out.filter((x) => isStaleFolder(x));
    if (nearlyOnly) out = out.filter((x) => folderProgress(x).nearlyDone);
    if (!showFinished) out = out.filter((x) => folderRemovableCount(x) > 0);
    if (q) out = out.filter((x) => (x.sourceFolderName ?? 'No folder').toLowerCase().includes(q));
    return out;
  }, [folders, sort, staleOnly, nearlyOnly, showFinished, search]);

  // Row order on screen; the "No folder" bucket has no id and can't be picked.
  const visibleIds = useMemo(
    () => visible.map((x) => x.sourceFolderPublicId).filter((id): id is string => !!id),
    [visible],
  );
  const closableIds = useMemo(
    () => new Set(visible.filter(isClosableFolder).map((x) => x.sourceFolderPublicId!)),
    [visible],
  );
  const range = useRangeSelection({
    visible: visibleIds,
    isSelected: (id) => picked.has(id),
    onApply: applyToSet(setPicked),
    isDisabled: (id) => !closableIds.has(id),
  });
  // Only what's on screen gets closed: a folder ticked, then hidden by the
  // search or a filter, isn't part of "Close N folders".
  const pickedFolders = visible.filter((x) => isClosableFolder(x) && picked.has(x.sourceFolderPublicId!));

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading folders…</p>;
  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Couldn't load folders: {error.message}
      </p>
    );
  }
  if (folders.length === 0) {
    return (
      <EmptyState
        icon={<FolderOpen className="h-6 w-6" />}
        title="Nothing has been exported from discovery yet."
        hint="Export a discovery folder to fill the queue."
      />
    );
  }

  return (
    <section aria-label="Folders" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>
            <strong className="font-semibold text-foreground">{totals.open.toLocaleString()}</strong> open
          </span>
          <span>
            <strong className="font-semibold text-foreground">{totals.messaged.toLocaleString()}</strong> messaged
          </span>
          <span>
            <strong className="font-semibold text-foreground">{totals.logged.toLocaleString()}</strong> logged
          </span>
          {(staleCount > 0 || staleOnly) && (
            <button
              type="button"
              onClick={() => onStaleOnlyChange(!staleOnly)}
              aria-pressed={staleOnly}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-pending-strong transition-colors hover:bg-warning/15',
                staleOnly && 'bg-warning/15 ring-1 ring-warning/40',
              )}
              title={`Folders with open leads whose oldest untouched lead is ${STALE_FOLDER_DAYS}+ days old`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {plural(staleCount, 'folder')} untouched for {STALE_FOLDER_DAYS}+ days
            </button>
          )}
          {(nearlyCount > 0 || nearlyOnly) && (
            <button
              type="button"
              onClick={() => onNearlyOnlyChange(!nearlyOnly)}
              aria-pressed={nearlyOnly}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-success-strong transition-colors hover:bg-success/15',
                nearlyOnly && 'bg-success/15 ring-1 ring-success/40',
              )}
              title={`Folders ${NEARLY_DONE_PERCENT}%+ worked with a few leads nobody has messaged yet`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {plural(nearlyCount, 'folder')} nearly done
            </button>
          )}
          {finishedCount > 0 && (
            <label
              className="inline-flex cursor-pointer items-center gap-1.5"
              title="Folders where every lead is logged or removed. Totals always count them."
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--brand)]"
                checked={showFinished}
                onChange={(e) => setShowFinished(e.target.checked)}
              />
              Show {plural(finishedCount, 'finished folder')}
            </label>
          )}
        </div>
        {folders.length > FOLDER_SEARCH_THRESHOLD && (
          <Input
            type="search"
            className="w-full md:w-56"
            aria-label="Find a folder"
            placeholder="Find a folder…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </div>

      {staleOnly && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <span>
            {staleCount === 0
              ? `No folders have open leads waiting ${STALE_FOLDER_DAYS}+ days.`
              : `Showing ${plural(staleCount, 'folder')} with open leads nobody has touched for ${STALE_FOLDER_DAYS}+ days, oldest first.`}
          </span>
          <Button variant="ghost" size="sm" onClick={() => onStaleOnlyChange(false)}>
            Show all folders
          </Button>
        </div>
      )}

      {nearlyOnly && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-sm">
          <span>
            {nearlyCount === 0
              ? `No folders are ${NEARLY_DONE_PERCENT}%+ worked with leads still left.`
              : `Showing ${plural(nearlyCount, 'folder')} ${NEARLY_DONE_PERCENT}%+ worked. Check what's left before closing — nothing closes on its own.`}
          </span>
          <Button variant="ghost" size="sm" onClick={() => onNearlyOnlyChange(false)}>
            Show all folders
          </Button>
        </div>
      )}

      {pickedFolders.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm"
          role="group"
          aria-label="Folder actions"
        >
          <span className="flex flex-wrap items-baseline gap-x-3">
            {plural(pickedFolders.length, 'folder')} selected
            <RangeSelectHint />
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setClosing(pickedFolders)}>
              <Archive className="mr-1 h-3.5 w-3.5" /> Close {plural(pickedFolders.length, 'folder')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPicked(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* One notice instead of a disabled picker on every folder. */}
      {campaignsDown && (
        <p className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <Megaphone className="h-3.5 w-3.5 shrink-0" />
          Clipping isn't answering, so campaigns can't be linked or shown right now. Briefing text can still be edited.
        </p>
      )}

      {visible.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">
          {search.trim()
            ? `No folder matches “${search}”.`
            : !showFinished && finishedCount > 0
              ? 'Every folder is finished. Show finished folders to see them.'
              : 'No folders to show.'}
        </p>
      ) : (
        <DataTable aria-label="Folders">
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>
                <span className="inline-flex items-center gap-2">
                  <SelectAllCheckbox
                    label="Select every folder shown that still has leads to remove"
                    state={range.headState}
                    disabled={range.selectable.length === 0}
                    onToggle={range.toggleAll}
                  />
                  Folder
                </span>
              </DataTableHeaderCell>
              <DataTableHeaderCell aria-sort={sort === 'newest' ? 'descending' : 'none'}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() => onSortChange(sort === 'newest' ? 'attention' : 'newest')}
                  title={sort === 'newest' ? 'Newest first. Click to sort by what needs attention.' : 'Sort by most recently released'}
                >
                  Released
                  {sort === 'newest' ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-60" />}
                </button>
              </DataTableHeaderCell>
              <DataTableHeaderCell aria-sort={sort === 'progress' ? 'descending' : 'none'}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() => onSortChange(sort === 'progress' ? 'attention' : 'progress')}
                  title={
                    sort === 'progress'
                      ? 'Nearly done first. Click to sort by what needs attention.'
                      : 'Sort by how much is worked (messaged, logged or removed), nearly done first'
                  }
                >
                  Worked
                  {sort === 'progress' ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-60" />}
                </button>
              </DataTableHeaderCell>
              <DataTableHeaderCell numeric title="Nobody has messaged these yet">Open</DataTableHeaderCell>
              <DataTableHeaderCell numeric title="Someone messaged them; no reply logged yet">Messaged</DataTableHeaderCell>
              <DataTableHeaderCell numeric title="A reply was logged, so an invite exists">Logged</DataTableHeaderCell>
              <DataTableHeaderCell aria-sort={sort === 'oldest' ? 'descending' : 'none'}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() => onSortChange(sort === 'oldest' ? 'attention' : 'oldest')}
                  title={sort === 'oldest' ? 'Sorted by oldest untouched lead. Click to sort by what needs attention.' : 'Sort by oldest untouched lead'}
                >
                  Oldest untouched
                  {sort === 'oldest' ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-60" />}
                </button>
              </DataTableHeaderCell>
              <DataTableHeaderCell>Briefing</DataTableHeaderCell>
              <DataTableHeaderCell>
                <span className="sr-only">Actions</span>
              </DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {visible.map((x) => {
              const stale = isStaleFolder(x);
              const messaged = folderMessagedCount(x);
              const unmessaged = folderUnmessagedCount(x);
              const removable = folderRemovableCount(x);
              const finished = removable === 0;
              const id = x.sourceFolderPublicId;
              const closable = isClosableFolder(x);
              const on = !!id && picked.has(id);
              const reason = !id ? NO_FOLDER_REASON : finished ? FINISHED_FOLDER_REASON : undefined;
              return (
                <DataTableRow
                  key={id ?? 'unfiled'}
                  state={on ? 'selected' : 'default'}
                  className={cn(finished && 'opacity-60', selectableRowClass(!closable))}
                  title={reason}
                  {...(id ? range.rowProps(id) : { 'aria-disabled': true })}
                >
                  <DataTableCell anchor>
                    <div className="flex items-center gap-2">
                      {id ? (
                        <RowCheckbox
                          aria-label={`Select ${x.sourceFolderName ?? 'folder'}`}
                          title={reason}
                          {...range.checkboxProps(id)}
                        />
                      ) : (
                        <RowCheckbox aria-label="Select No folder" title={reason} checked={false} disabled readOnly />
                      )}
                      <button
                        type="button"
                        onClick={() => onViewLeads(id)}
                        className="inline-flex max-w-[22rem] items-center gap-2 text-left hover:underline"
                        title="View this folder's leads"
                      >
                        <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                        <span className="truncate">{x.sourceFolderName ?? 'No folder'}</span>
                        {folderPurposeOf(x.purpose) !== 'clipping' && (
                          <Pill tone="muted">{FOLDER_PURPOSE_LABELS[folderPurposeOf(x.purpose)]}</Pill>
                        )}
                      </button>
                    </div>
                  </DataTableCell>
                  <DataTableCell muted data-label="Released" title={formatDateTime(x.lastExportedAt)}>
                    {formatDate(x.lastExportedAt)}
                  </DataTableCell>
                  <DataTableCell data-label="Worked">
                    <FolderProgressCell folder={x} />
                  </DataTableCell>
                  <DataTableCell numeric data-label="Open">
                    {x.available + x.claimed > 0 ? x.available + x.claimed : '—'}
                  </DataTableCell>
                  <DataTableCell numeric data-label="Messaged">
                    {messaged > 0 ? messaged : '—'}
                  </DataTableCell>
                  <DataTableCell numeric data-label="Logged">
                    {x.promoted > 0 ? x.promoted : '—'}
                  </DataTableCell>
                  <DataTableCell
                    data-label="Oldest untouched"
                    className={stale ? 'font-medium text-pending-strong' : 'text-muted-foreground'}
                  >
                    {x.available > 0 ? formatDayCount(x.oldestUnclaimedDays) : '—'}
                  </DataTableCell>
                  <DataTableCell muted data-label="Briefing">
                    {x.campaigns.length > 0 ? (
                      <span className="inline-flex max-w-[16rem] flex-wrap gap-1">
                        {x.campaigns.map((c) => (
                          <Tag key={c.id} tone="neutral" icon={false} title={c.description ?? c.name}>
                            {c.name}
                            {c.status === 'pending' && ' · soon'}
                          </Tag>
                        ))}
                      </span>
                    ) : (x.campaignIds ?? []).length > 0 ? (
                      <span title="Linked to campaigns that didn't resolve">{plural(x.campaignIds.length, 'campaign')}</span>
                    ) : (
                      '—'
                    )}
                  </DataTableCell>
                  <DataTableCell actions>
                    <div className="flex justify-end gap-1">
                      {id && folderProgress(x).nearlyDone ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onReviewLeftovers(id)}
                          title="See the leads nobody has messaged yet, with any irrelevant notes, and remove the bad ones"
                        >
                          <ListChecks className="mr-1 h-3.5 w-3.5" /> Review leftovers
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => onViewLeads(x.sourceFolderPublicId)}>
                          Leads
                        </Button>
                      )}
                      {x.sourceFolderPublicId && (
                        <Button variant="ghost" size="sm" onClick={() => setEditing(x)}>
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Briefing
                        </Button>
                      )}
                      {closable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setClosing([x])}
                          title={
                            unmessaged > 0
                              ? `Remove the ${plural(unmessaged, 'lead')} nobody has messaged yet` +
                                (messaged > 0 ? ` (optionally the ${messaged} messaged too)` : '')
                              : `Remove the ${plural(messaged, 'messaged lead')} still waiting on a reply`
                          }
                        >
                          <Archive className="mr-1 h-3.5 w-3.5" /> Close
                        </Button>
                      )}
                    </div>
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        </DataTable>
      )}

      {closing && (
        <CloseFoldersDialog
          folders={closing}
          onClose={() => setClosing(null)}
          onDone={() => {
            setClosing(null);
            setPicked(new Set());
          }}
        />
      )}

      {editing?.sourceFolderPublicId && (
        <GuidanceDialog
          key={editing.sourceFolderPublicId}
          folder={editing}
          campaignsDown={campaignsDown}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

/**
 * How far through the folder the team is: a slim bar, the percent worked,
 * what's left, and — once it's nearly done — a plain read on whether the
 * leftovers are junk or worth a look. The full breakdown is the tooltip.
 * A summary without `flaggedUnmessaged` (older backend) shows no flag count
 * and no verdict rather than claiming zero.
 */
function FolderProgressCell({ folder }: { folder: AdminFolderSummary }): JSX.Element {
  const p = folderProgress(folder);
  if (p.percent === null) return <span className="text-muted-foreground">—</span>;
  const verdict = p.nearlyDone ? leftoverVerdict(folder, STALE_FOLDER_DAYS) : null;
  return (
    <div className="flex min-w-[9rem] flex-col gap-1" title={leftoverBreakdown(folder)}>
      <div className="flex items-center gap-2">
        <div
          className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-label="Worked"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={p.percent}
        >
          <div
            className={cn('h-full rounded-full', p.left === 0 || p.nearlyDone ? 'bg-success' : 'bg-brand')}
            style={{ width: `${p.percent}%` }}
          />
        </div>
        <span className="font-mono text-xs tabular-nums">{p.percent}%</span>
        {p.nearlyDone && (
          <Pill tone="success" className="whitespace-nowrap">
            Nearly done
          </Pill>
        )}
      </div>
      {p.left > 0 && (
        <span className="text-xs text-muted-foreground">
          {p.left} left
          {p.flaggedLeft !== null && p.flaggedLeft > 0 && (
            <span className="text-pending-strong"> · {p.flaggedLeft} irrelevant</span>
          )}
        </span>
      )}
      {verdict && (
        <span className={cn('text-xs', verdict === 'junk' ? 'text-success-strong' : 'text-pending-strong')}>
          {leftoverVerdictText(verdict, STALE_FOLDER_DAYS)}
        </span>
      )}
    </div>
  );
}

/**
 * A folder can be closed while it still has leads to remove — Open, Claimed
 * or Messaged and waiting on a reply. Old folders usually have nothing Open
 * left but still sit on outreachers' queues through their messaged leads.
 */
function isClosableFolder(x: AdminFolderSummary): boolean {
  return !!x.sourceFolderPublicId && folderRemovableCount(x) > 0;
}
const FINISHED_FOLDER_REASON = 'Nothing left to remove — every lead is logged or removed';
const NO_FOLDER_REASON = 'Leads without a folder can’t be closed as a group';

/**
 * Close one or more folders: every lead nobody has messaged (Open + Claimed)
 * is removed from the queue, and optionally the messaged leads still waiting
 * on a reply too, so the folder drops off outreachers' lists. Logged leads
 * are never touched (the server refuses). Removed leads keep their history
 * and can be restored from the Leads tab.
 */
function CloseFoldersDialog({
  folders,
  onClose,
  onDone,
}: {
  folders: AdminFolderSummary[];
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const close = useCloseFolder();
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const unmessaged = folders.reduce((n, x) => n + folderUnmessagedCount(x), 0);
  const waiting = folders.reduce((n, x) => n + folderMessagedCount(x), 0);
  // With nothing unmessaged left (the usual old folder), closing without the
  // messaged leads would do nothing — so it starts ticked.
  const [includeMessaged, setIncludeMessaged] = useState(unmessaged === 0);
  const removeCount = (x: AdminFolderSummary): number =>
    folderUnmessagedCount(x) + (includeMessaged ? folderMessagedCount(x) : 0);
  const leads = folders.reduce((n, x) => n + removeCount(x), 0);
  const removing = [
    unmessaged > 0 && `the ${plural(unmessaged, 'lead')} nobody has messaged yet`,
    includeMessaged && waiting > 0 && `the ${plural(waiting, 'messaged lead')} still waiting on a reply`,
  ].filter((x): x is string => !!x);
  const title =
    folders.length === 1 ? `Close “${folders[0]?.sourceFolderName ?? 'folder'}”?` : `Close ${plural(folders.length, 'folder')}?`;

  async function submit(): Promise<void> {
    if (running) return;
    setRunning(true);
    let removed = 0;
    let failed = 0;
    for (const x of folders) {
      if (!x.sourceFolderPublicId || removeCount(x) === 0) continue;
      try {
        const res = await close.mutateAsync({
          folderPublicId: x.sourceFolderPublicId,
          reason: reason.trim() || 'Folder closed',
          ...(includeMessaged ? { statuses: CLOSE_WITH_MESSAGED } : {}),
        });
        removed += res?.dismissed ?? 0;
      } catch {
        failed += 1;
      }
    }
    setRunning(false);
    if (failed === 0 || removed > 0) {
      toast.success(includeMessaged ? `Removed ${plural(removed, 'lead')}` : `Removed ${plural(removed, 'lead')} nobody had messaged`);
    }
    if (failed > 0) toast.error(`${plural(failed, 'folder')} couldn't be closed. Try again.`);
    onDone();
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !running && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {removing.length > 0
                ? `Removes ${removing.join(' and ')}, so outreachers stop seeing ${folders.length === 1 ? 'this folder' : 'these folders'} in their queue.`
                : 'Nobody is left unmessaged, so only the messaged leads can go.'}
              {!includeMessaged && waiting > 0 && unmessaged > 0 && ` ${plural(waiting, 'messaged lead')} still waiting on a reply stay until they're done.`}{' '}
              Logged leads are never touched. Removed leads keep their message history and can be restored from the
              Leads tab (status Removed) and messaged again.
            </DialogDescription>
          </DialogHeader>
          {waiting > 0 && (
            <label className="flex cursor-pointer items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--brand)]"
                checked={includeMessaged}
                onChange={(e) => setIncludeMessaged(e.target.checked)}
              />
              <span>
                Also remove the {plural(waiting, 'messaged lead')} still waiting on a reply
                <span className="block text-xs text-muted-foreground">
                  Outreachers stop following them up here. They can be restored and re-messaged later.
                </span>
              </span>
            </label>
          )}
          {folders.length > 1 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
              {folders.map((x) => (
                <li key={x.sourceFolderPublicId ?? 'unfiled'} className="flex justify-between gap-3">
                  <span className="truncate">{x.sourceFolderName ?? 'No folder'}</span>
                  <span className="tabular-nums text-muted-foreground">{removeCount(x)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="close-reason">Reason (optional)</Label>
            <Input
              id="close-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. old, not a priority"
              maxLength={80}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button type="submit" disabled={running || leads === 0}>
              {running ? 'Closing…' : `Remove ${plural(leads, 'lead')}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Edit a folder's briefing: campaigns to link (ids only, names resolve
 * live), best campaigns and how to pitch. Outreachers see it as the
 * "Briefing for <folder>" strip on their Lead queue.
 */
function GuidanceDialog({
  folder,
  campaignsDown,
  onClose,
}: {
  folder: AdminFolderSummary;
  campaignsDown: boolean;
  onClose: () => void;
}): JSX.Element {
  const folderId = folder.sourceFolderPublicId ?? '';
  const guidance = useFolderGuidance(folderId);
  const campaigns = useSelectableCampaigns();
  const save = useSaveFolderGuidance();
  const [recommended, setRecommended] = useState<string | null>(null);
  const [pitch, setPitch] = useState<string | null>(null);
  const [campaignIds, setCampaignIds] = useState<string[] | null>(null);

  const live = campaigns.data?.campaigns ?? [];
  const liveIds = new Set(live.map((c) => c.id));
  const storedIds = folder.campaignIds ?? [];
  // Ids we can't resolve are carried through every save so a picker that
  // can't SHOW a link never silently DELETES it.
  const unresolved = storedIds.filter((id) => !liveIds.has(id));
  const selectedIds = campaignIds ?? storedIds.filter((id) => liveIds.has(id));
  const recommendedValue = recommended ?? guidance.data?.recommendedCampaigns ?? '';
  const pitchValue = pitch ?? guidance.data?.pitchNotes ?? '';

  function submit(): void {
    save.mutate(
      {
        folderPublicId: folderId,
        sourceFolderName: folder.sourceFolderName,
        ...(recommended !== null ? { recommendedCampaigns: recommended.trim() || null } : {}),
        ...(pitch !== null ? { pitchNotes: pitch.trim() || null } : {}),
        ...(campaignIds !== null ? { campaignIds: [...campaignIds, ...unresolved] } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Briefing saved');
          onClose();
        },
        onError: (err: Error) => toast.error(`Couldn't save the briefing: ${err.message}`),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-lg"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Briefing for {folder.sourceFolderName ?? 'this folder'}</DialogTitle>
          <DialogDescription>
            Outreachers see this above the folder's leads, before they write a DM.
          </DialogDescription>
        </DialogHeader>
        {guidance.isLoading ? (
          <p className="py-4 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Campaigns</Label>
              {campaignsDown ? (
                <p className="text-xs text-muted-foreground">
                  Campaigns can't be loaded right now. Existing links are kept.
                </p>
              ) : (
                <MultiSelect
                  value={selectedIds}
                  onChange={setCampaignIds}
                  options={live.map((c) => ({
                    value: c.id,
                    label: c.status === 'pending' ? `${c.name} (starting soon)` : c.name,
                  }))}
                  placeholder={campaigns.isLoading ? 'Loading…' : live.length === 0 ? 'No live campaigns' : 'Link campaigns'}
                  disabled={campaigns.isLoading || live.length === 0}
                  showClear
                  clearLabel="Unlink all"
                />
              )}
              {unresolved.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  +{unresolved.length} linked campaign{unresolved.length === 1 ? '' : 's'} not live or not returned by Clipping.
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="guidance-recommended">Best campaigns</Label>
              <textarea
                id="guidance-recommended"
                rows={2}
                maxLength={2000}
                value={recommendedValue}
                onChange={(e) => setRecommended(e.target.value)}
                className="rounded-lg border border-input bg-transparent px-3 py-2 text-sm"
                placeholder="Which campaigns to push these creators toward"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="guidance-pitch">How to pitch them</Label>
              <textarea
                id="guidance-pitch"
                rows={4}
                maxLength={4000}
                value={pitchValue}
                onChange={(e) => setPitch(e.target.value)}
                className="rounded-lg border border-input bg-transparent px-3 py-2 text-sm"
                placeholder="What to say in the DM"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={save.isPending || guidance.isLoading}>
            {save.isPending ? 'Saving…' : 'Save briefing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Lead row ──────────────────────────────────────────────────────────────

function ProspectRow({
  prospect,
  teamName,
  selected,
  rowProps,
  checkboxProps,
}: {
  prospect: AdminProspect;
  teamName: (code: string) => string;
  selected: boolean;
  rowProps: RangeRowProps;
  checkboxProps: RangeCheckboxProps;
}): JSX.Element {
  const undismiss = useUndismissProspect();
  const locked = isLoggedLead(prospect);
  const inQueueDays = daysSince(prospect.createdAt);
  const stale = prospect.status === 'AVAILABLE' && (inQueueDays ?? 0) >= STALE_FOLDER_DAYS;
  const lastMessageDays = prospect.lastContactedAt ? daysSince(prospect.lastContactedAt) : null;

  const owner =
    prospect.claimedByInviterName ??
    prospect.assignedToInviterName ??
    (prospect.assignedToTeam ? `${teamName(prospect.assignedToTeam)} team` : null);

  const contacts = prospect.contactedBy ?? [];
  const flags = prospect.flaggedBy ?? [];
  const firstNote = flags.find((x) => x.note)?.note ?? null;
  const handle = `@${prospect.targetIdentifier}`;
  const profileHref = prospectProfileUrl(prospect);

  return (
    <DataTableRow
      state={selected ? 'selected' : 'default'}
      className={selectableRowClass(locked)}
      title={locked ? LOGGED_LEAD_REASON : undefined}
      {...rowProps}
    >
      <DataTableCell anchor>
        <div className="flex items-center gap-2">
          <RowCheckbox aria-label={`Select ${handle}`} title={locked ? LOGGED_LEAD_REASON : undefined} {...checkboxProps} />
          <PlatformIcon platform={prospect.channel} size={14} className="text-muted-foreground" />
          {profileHref ? (
            <a
              href={profileHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              title="Open their profile in a new tab"
            >
              {handle}
            </a>
          ) : (
            <span>{handle}</span>
          )}
          {locked && (
            <Link
              to={inviteLink({ handle: prospect.targetIdentifier })}
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              title="Open the logged invite"
            >
              Invite
            </Link>
          )}
        </div>
      </DataTableCell>
      <DataTableCell data-label="Status">
        <Pill tone={STATUS_TONES[prospect.status] ?? 'muted'}>{prospectStatusLabel(prospect.status)}</Pill>
      </DataTableCell>
      <DataTableCell muted data-label="Who has it">
        {owner ?? 'Anyone'}
      </DataTableCell>
      {/* Who has DM'd, and when. A Messaged lead with a growing count and an
          old first message is the "messaged but never replied" case. */}
      <DataTableCell data-label="Messaged by">
        {contacts.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="cursor-help" title={contacts.map((c) => c.inviterName).join(', ')}>
            <span className="font-medium">{contacts.length}</span>
            <span className="text-muted-foreground">
              {' '}
              · {contacts[0]?.inviterName}
              {contacts.length > 1 ? ` +${contacts.length - 1}` : ''}
            </span>
          </span>
        )}
      </DataTableCell>
      {/* Outreachers calling this lead wrong for its folder. */}
      <DataTableCell data-label="Irrelevant">
        {flags.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span
            className="inline-flex cursor-help items-center gap-1 text-pending-strong"
            title={flags.map((x) => (x.note ? `${x.inviterName}: ${x.note}` : x.inviterName)).join('\n')}
          >
            <EyeOff className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="font-medium">{flags.length}</span>
            {firstNote && <span className="max-w-[160px] truncate text-muted-foreground">· {firstNote}</span>}
          </span>
        )}
      </DataTableCell>
      <DataTableCell muted data-label="Folder">
        {prospect.sourceFolderName ?? '—'}
      </DataTableCell>
      <DataTableCell
        data-label="In queue for"
        className={stale ? 'text-pending-strong' : 'text-muted-foreground'}
        title={`Exported ${formatDateTime(prospect.createdAt)}`}
      >
        {formatDayCount(inQueueDays)}
      </DataTableCell>
      <DataTableCell
        muted
        data-label="Since last message"
        title={
          prospect.lastContactedAt
            ? `Last messaged ${formatDateTime(prospect.lastContactedAt)}`
            : contacts.length > 0
              ? 'Messaged, but the time of the last message isn’t available here'
              : 'Not messaged yet'
        }
      >
        {formatDayCount(lastMessageDays)}
      </DataTableCell>
      <DataTableCell actions>
        {prospect.status === 'DISMISSED' && (
          <Button
            variant="ghost"
            size="sm"
            disabled={undismiss.isPending}
            aria-label={`Restore ${handle} to the queue`}
            onClick={() =>
              undismiss.mutate(prospect.publicId, {
                onSuccess: () => toast.success(`${handle} is back in the queue`),
                onError: (err: Error) => toast.error(err.message),
              })
            }
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
          </Button>
        )}
      </DataTableCell>
    </DataTableRow>
  );
}

/**
 * Logged leads have a real invite pointing back at them; removing or
 * deleting would desync the two (the server refuses), so they can't be selected.
 */
function isLoggedLead(p: Pick<AdminProspect, 'status'>): boolean {
  return p.status === 'PROMOTED';
}
const LOGGED_LEAD_REASON = 'Already logged as an invite, so it can’t be removed or deleted';

// ── Bulk actions ──────────────────────────────────────────────────────────

type ReassignTarget = 'outreacher' | 'team' | 'pool';

function reportSkipped(skipped: number, why: string): void {
  if (skipped > 0) toast.warning(`${plural(skipped, 'lead')} skipped — ${why}`);
}

function BulkActionDialog({
  kind,
  publicIds,
  teams,
  onClose,
  onDone,
}: {
  kind: BulkKind;
  publicIds: string[];
  teams: OutreachTeam[];
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const dismiss = useDismissProspects();
  const remove = useDeleteProspects();
  const reassign = useReassignProspects();
  const [reason, setReason] = useState('');
  const [typedCount, setTypedCount] = useState('');
  const [target, setTarget] = useState<ReassignTarget>('outreacher');
  const [team, setTeam] = useState('');
  const [outreacher, setOutreacher] = useState<{ id: number; name: string } | null>(null);

  const count = publicIds.length;
  const leads = plural(count, 'lead');
  const pending = dismiss.isPending || remove.isPending || reassign.isPending;
  const onError = (err: Error): void => {
    toast.error(err.message);
  };

  const canSubmit =
    !pending &&
    count > 0 &&
    (kind === 'delete'
      ? confirmCountMatches(typedCount, count)
      : kind === 'reassign'
        ? target === 'pool' || (target === 'team' ? !!team : !!outreacher)
        : true);

  function submit(): void {
    if (!canSubmit) return;
    if (kind === 'remove') {
      dismiss.mutate(
        { publicIds, reason: reason.trim() || undefined },
        {
          onSuccess: (res) => {
            const done = res?.dismissed ?? [];
            toast.success(`Removed ${plural(done.length, 'lead')} from the queue`);
            reportSkipped(skippedIds(publicIds, done).length, 'already logged as invites');
            onDone();
          },
          onError,
        },
      );
    } else if (kind === 'delete') {
      remove.mutate(publicIds, {
        onSuccess: (res) => {
          const done = res?.deleted ?? [];
          toast.success(`Deleted ${plural(done.length, 'lead')}`);
          reportSkipped(skippedIds(publicIds, done).length, 'already logged as invites, so they were kept');
          onDone();
        },
        onError,
      });
    } else {
      const body =
        target === 'outreacher'
          ? { publicIds, assignedToInviterId: outreacher?.id ?? null }
          : target === 'team'
            ? { publicIds, assignedToTeam: team, assignedToInviterId: null }
            : { publicIds, assignedToTeam: null, assignedToInviterId: null };
      const where =
        target === 'outreacher'
          ? outreacher?.name ?? 'the outreacher'
          : target === 'team'
            ? `${teamLabel(team, teams.find((t) => t.code === team)?.displayName)} team`
            : 'the general pool';
      reassign.mutate(body, {
        onSuccess: (res) => {
          const done = res?.reassigned ?? [];
          toast.success(`Gave ${plural(done.length, 'lead')} to ${where}`);
          reportSkipped(skippedIds(publicIds, done).length, "they couldn't be reassigned");
          onDone();
        },
        onError,
      });
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>
              {kind === 'remove' && `Remove ${leads} from the queue?`}
              {kind === 'delete' && `Delete ${leads} permanently?`}
              {kind === 'reassign' && `Reassign ${leads}`}
            </DialogTitle>
            <DialogDescription>
              {kind === 'remove' &&
                'Outreachers stop seeing them. They stay on record as Removed and can be restored. Logged leads are skipped.'}
              {kind === 'delete' &&
                'This can’t be undone and keeps no record. Use Remove instead unless these were exported by mistake. Logged leads are skipped.'}
              {kind === 'reassign' && 'Choose who should work these leads.'}
            </DialogDescription>
          </DialogHeader>

          {kind === 'remove' && (
            <div className="grid gap-1.5">
              <Label htmlFor="remove-reason">Reason (optional)</Label>
              <Input
                id="remove-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. wrong niche"
                maxLength={80}
              />
            </div>
          )}

          {kind === 'delete' && (
            <div className="grid gap-1.5">
              <Label htmlFor="delete-count">
                Type <span className="font-semibold tabular-nums">{count}</span> to confirm
              </Label>
              <Input
                id="delete-count"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={typedCount}
                onChange={(e) => setTypedCount(e.target.value)}
                aria-describedby="delete-count-hint"
              />
              <p id="delete-count-hint" className="text-xs text-muted-foreground">
                The number of leads you selected.
              </p>
            </div>
          )}

          {kind === 'reassign' && (
            <div className="space-y-3">
              <fieldset className="grid gap-1.5">
                <legend className="mb-1 text-sm font-medium">Give them to</legend>
                {(
                  [
                    ['outreacher', 'One outreacher'],
                    ['team', 'A team'],
                    ['pool', 'Everyone (general pool)'],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="reassign-target"
                      className="accent-[var(--primary)]"
                      checked={target === value}
                      onChange={() => setTarget(value)}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>

              {target === 'outreacher' && (
                <OutreacherSearchPicker value={outreacher} onChange={setOutreacher} />
              )}
              {target === 'team' && (
                <div className="grid gap-1.5">
                  <Label>Team</Label>
                  <Combobox
                    value={team}
                    onChange={setTeam}
                    options={teams.filter((t) => t.isActive).map((t) => ({ value: t.code, label: t.displayName }))}
                    placeholder="Pick a team"
                  />
                  <p className="text-xs text-muted-foreground">
                    Anyone on the team can pick these up. Any outreacher they were given to is cleared.
                  </p>
                </div>
              )}
              {target === 'pool' && (
                <p className="text-xs text-muted-foreground">
                  Clears the outreacher and team, so every outreacher can see these leads.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant={kind === 'delete' ? 'destructive' : 'default'} disabled={!canSubmit}>
              {pending
                ? 'Working…'
                : kind === 'delete'
                  ? `Delete ${leads}`
                  : kind === 'remove'
                    ? `Remove ${leads}`
                    : 'Reassign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Server-side outreacher search: type a name or email, pick one. */
function OutreacherSearchPicker({
  value,
  onChange,
}: {
  value: { id: number; name: string } | null;
  onChange: (v: { id: number; name: string } | null) => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search.trim(), 300);
  const results = useAdminInvitersPaged({ isActive: true, search: debounced || undefined, limit: 20 });
  const rows = results.data?.data ?? [];

  return (
    <div className="grid gap-1.5">
      <Label htmlFor="reassign-outreacher">Outreacher</Label>
      <Input
        id="reassign-outreacher"
        type="search"
        autoComplete="off"
        placeholder="Search by name or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {value && (
        <p className="text-xs">
          Selected: <span className="font-medium">{value.name}</span>{' '}
          <button type="button" className="text-muted-foreground underline" onClick={() => onChange(null)}>
            change
          </button>
        </p>
      )}
      <ul role="listbox" aria-label="Matching outreachers" className="max-h-48 overflow-y-auto rounded-md border">
        {results.isLoading ? (
          <li className="px-3 py-2 text-xs text-muted-foreground">Searching…</li>
        ) : results.error ? (
          <li className="px-3 py-2 text-xs text-destructive">{(results.error as Error).message}</li>
        ) : rows.length === 0 ? (
          <li className="px-3 py-2 text-xs text-muted-foreground">No active outreacher matches.</li>
        ) : (
          rows.map((i) => {
            const isSel = value?.id === i.id;
            return (
              <li key={i.id} role="option" aria-selected={isSel}>
                <button
                  type="button"
                  onClick={() => onChange({ id: i.id, name: i.name })}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent',
                    isSel && 'bg-accent',
                  )}
                >
                  <span className="truncate">{i.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{teamLabel(i.team)}</span>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
